import React, { useState, useEffect, useCallback } from 'react';
import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

import useEditorUpdater from 'generics/hooks/use-editor-updater';

import { series } from 'utilities';

import BasicTextEditor from '../../';
import HelptapEditor from '../../helpers';
import elementTypes from '../types';

import SynchronizedPageRegionOptions from './options';

import './index.scss';

function SynchronizedPageRegion(props) {

  const element = props.element;
  const synchronizedPageRegionId = element.synchronizedPageRegionId;

  const editor = useSlateStatic();

  const elementPath = ReactEditor.findPath(editor, element);

  const [rootState, setRootState] = useState({
    synchronizedPageRegion: null,
    lastUpdatedAt: Date.now()
  });

  /* beautify ignore:start */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { 
    handleContentChange,
  } = useEditorUpdater(useCallback(() => rootState.synchronizedPageRegion?.updateKey, [rootState.synchronizedPageRegion?.updateKey]), updateContent);
  /* beautify ignore:end */

  useEffect(function onLoad() {

    const eventRegisterers = [
      registerSynchronizedPageRegionUpdatedEventHandler,
      registerSynchronizedPageRegionDeletedEventHandler
    ];

    const eventDeregisterers = series(eventRegisterers);

    initialize();

    return function unregisterEventHandlers() {
      series(eventDeregisterers);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* beautify ignore:start */
  useEffect(function onNodesChange() {

    if (rootState.synchronizedPageRegion === null) {
      return;
    }

    handleContentChange(rootState.synchronizedPageRegion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootState.synchronizedPageRegion?.nodes]);
  /* beautify ignore:end */

  function setSynchronizedPageRegion(synchronizedPageRegion) {

    setRootState(function updateRootState(rootState) {

      const _synchronizedPageRegion = rootState.synchronizedPageRegion;

      // if default state, set new state
      if (_synchronizedPageRegion === null) {
        return {
          ...rootState,
          synchronizedPageRegion: {
            ...rootState.synchronizedPageRegion,
            ...synchronizedPageRegion
          }
        };
      }

      // if nodes are changed from this reference
      if (element.id === synchronizedPageRegion.lastSourceId) {

        // if nodes are changed from this instance, `rootState.lastUpdatedAt` 
        // would be changed before.
        // if that is changed, then this instance have the latest node
        if (rootState.lastUpdatedAt >= synchronizedPageRegion.updatedAt) {
          return {
            ...rootState,
            synchronizedPageRegion: {
              ...rootState.synchronizedPageRegion,
              ..._synchronizedPageRegion
            }
          };
        }

      }

      // Otherwise, set the latest value
      return {
        ...rootState,
        synchronizedPageRegion: {
          ...rootState.synchronizedPageRegion,
          ...synchronizedPageRegion
        }
      };

    });

  }

  function setLastUpdatedAt(lastUpdatedAt) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        lastUpdatedAt
      };

    });

  }

  async function loadNodes() {

    try {

      const projection = ['nodes', 'updatedAt', 'updateKey'];

      const synchronizedPageRegion = await serverInterface.startup.synchronizedPageRegions.getSynchronizedPageRegionById(synchronizedPageRegionId, projection);

      if (Array.isArray(synchronizedPageRegion.nodes) === false || synchronizedPageRegion.nodes.length === 0) {
        throw new Error('Invalid nodes');
      }

      setSynchronizedPageRegion(synchronizedPageRegion);

    } catch (exception) {
      HelptapEditor.deleteNodeByPath(editor, elementPath);
    }

  }

  async function initialize() {

    await loadNodes();

  }

  function unsynchronize() {

    const nodes = rootState.synchronizedPageRegion.nodes;

    HelptapEditor.deleteNodeByPath(editor, elementPath);
    HelptapEditor.insertNodes(editor, nodes, { at: elementPath });

  }

  function registerSynchronizedPageRegionUpdatedEventHandler() {

    async function handleSynchronizedPageRegionUpdated(data) {

      if (data.pageId !== props.parentEntityId) {
        return;
      }

      data.synchronizedPageRegion.lastSourceId = data.lastSourceId;
      setSynchronizedPageRegion(data.synchronizedPageRegion);

    }

    application.eventSystem.internal.on(externalEventCodes.SYNCHRONIZED_PAGE_REGION_UPDATED + ':' + synchronizedPageRegionId, handleSynchronizedPageRegionUpdated);

    return function unregisterSynchronizedPageRegionUpdatedEventHandler() {

      application.eventSystem.internal.off(externalEventCodes.SYNCHRONIZED_PAGE_REGION_UPDATED + ':' + synchronizedPageRegionId, handleSynchronizedPageRegionUpdated);

    };

  }

  function registerSynchronizedPageRegionDeletedEventHandler() {

    function handleSynchronizedPageRegionDeleted(data) {

      HelptapEditor.deleteNodeByPath(editor, elementPath);

    }

    application.eventSystem.internal.on(externalEventCodes.SYNCHRONIZED_PAGE_REGION_DELETED + ':' + synchronizedPageRegionId, handleSynchronizedPageRegionDeleted);

    return function unregisterSynchronizedPageRegionDeletedEventHandler() {

      application.eventSystem.internal.off(externalEventCodes.SYNCHRONIZED_PAGE_REGION_DELETED + ':' + synchronizedPageRegionId, handleSynchronizedPageRegionDeleted);

    };

  }

  function storeContent(nodes) {

    setRootState(function updateRootState(rootState) {

      const synchronizedPageRegion = rootState.synchronizedPageRegion;

      return {
        ...rootState,
        synchronizedPageRegion: {
          ...synchronizedPageRegion,
          nodes
        }
      };

    });

  }

  async function updateContent(synchronizedPageRegion, updateKey) {

    const synchronizedPageRegionId = synchronizedPageRegion._id;
    const sourceId = element.id;

    const result = await serverInterface.startup.synchronizedPageRegions.updateSynchronizedPageRegionById(synchronizedPageRegionId, sourceId, synchronizedPageRegion.nodes, updateKey);

    if (result.updated === false) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'This synchronized block has been updated by another user. Your changes are overwritten.',
        seconds: 5,
        type: 'error'
      });
      setSynchronizedPageRegion(result.synchronizedPageRegion);
    } else {
      setLastUpdatedAt(result.updatedAt);
    }

    return result;

  }

  function tryRenderEditor() {

    if (rootState.synchronizedPageRegion === null) {
      return <ht-spinner size="small" />;
    }

    const synchronizedPageRegion = rootState.synchronizedPageRegion;

    const columnAttributes = {
      ...props,
      mediaUploader(file) {
        return props.mediaUploader(file, synchronizedPageRegion);
      },
      enableAdvancedOptions: true,
      enabledElements: [
        elementTypes.DEFAULT,
        elementTypes.BLOCK_QUOTE,
        elementTypes.BULLETED_LIST,
        elementTypes.HEADING_ONE,
        elementTypes.HEADING_TWO,
        elementTypes.HEADING_THREE,
        elementTypes.LINK,
        elementTypes.NUMBERED_LIST_ITEM,
        elementTypes.BULLETED_LIST_ITEM,
        elementTypes.NUMBERED_LIST,
        elementTypes.CHECK_LIST_ITEM,
        elementTypes.IMAGE,
        elementTypes.SPINNER,
        elementTypes.BUTTON,
        elementTypes.AUTHENTICATOR_BUTTON,
        elementTypes.URL_EMBED,
        elementTypes.DIVIDER,
        elementTypes.MENTION,
        elementTypes.POLL_EDITOR_VIEW,
        elementTypes.SINGLE_PRODUCER_MEETING,
        elementTypes.HT_FILE,
        elementTypes.HT_STARTUP_EVENT_TEMPLATE,
        elementTypes.HT_STARTUP_EVENT,
        elementTypes.PRODUCT,
        elementTypes.TABLE,
        elementTypes.MULTIPLE_COLUMNS
      ],
      value: synchronizedPageRegion.nodes,
      level: 0,
      onChange: storeContent,
      onSelectChange: undefined,
      disableAdvancedDragHandleFeatures: true,
      onEditorReady(editor) {
        // editorReference.current = editor;
      }
    };

    return <BasicTextEditor {...columnAttributes} key={synchronizedPageRegion.updatedAt} />;

  }

  function tryRenderOptions() {

    if (rootState.initialized === false) {
      return;
    }

    const synchronizedPageRegionOptionsPanelProperties = {
      element,
      onUnsynchronize: unsynchronize
    };

    return <SynchronizedPageRegionOptions {...synchronizedPageRegionOptionsPanelProperties} />;

  }

  function renderSynchronizedPageRegion() {

    const { attributes, children } = props;

    const synchronizedPageRegionAttributes = {
      ...attributes,
      className: 'synchronized-region',
      contentEditable: false,
      id: element.id
    };

    return <div {...synchronizedPageRegionAttributes}>
      {children}
      {tryRenderEditor()}
      {tryRenderOptions()}
    </div>;

  }

  function renderMain() {

    return renderSynchronizedPageRegion();

  }

  return renderMain();

}

export default SynchronizedPageRegion;